import moment from 'moment';
import { isCancel } from 'axios';
import { fetchWrapper } from 'helpers/helpers';
import history from 'helpers/history';
import { setLoading, setOpeningsLoading, setStepsLoading } from 'store/reducers/commonReducer';
import {
  setCandidatesJob,
  setJobInfo,
  setOpeningsList,
  setStepsToJob,
  clearCandidatesJob,
  setCandidatesLoader,
  setLoadingCandidateData,
  setApproversCandidate,
  setApproversCandidateLoading,
  setCVSCandidate,
  setCVSLinks,
  setScorecardCandidate,
  setInterviews,
  upsertInterview,
  removeInterview,
  clearInterviewData,
  setInterviewLoading,
  setOfferingCandidate,
  setOfferingCandidateLoading,
  setProbationPeriodData,
  setEntryDateLoading,
  setOpeningCandidate,
  setActivityList,
  setActivityListLoading,
  setRoomsOptions,
  setUserData,
  setOfferRecommendation,
  setTimelineCommentLoading,
  setTodaysnterviews,
  setInterviewFeedbacksLoading,
  setInterviewFeedbacks,
  setJobStepAssignmentId,
  updateUserData,
  candidateSummaryRequested,
  candidateSummaryUpdateRequested,
  candidateSummarySucceeded,
  candidateSummaryFailed,
  candidateSummaryUpdateFailed,
} from 'store/reducers/jobReducer';
import type {
  ActivityItem,
  CandidateSummary,
  HiredCandidate,
  Interview,
  InterviewFeedback,
  JobApprover,
  JobInfo,
  JobListItem,
  JobListLiteItem,
  JobScorecard,
  JobWorkflowStep,
  Opening,
  OpeningsCount,
  OfferingCandidate,
  ProbationPeriod,
  Room,
  TechnologyAreaOption,
  TodaysInterview,
} from 'store/reducers/jobReducer';
import type { PaginatedResponse, Uuid } from 'store/reducers/types';
import type { AppThunk } from 'store';
import { ROUTES } from 'router/constants';
import { getFeedbackHistorySteps, getInterviewKind, normalizeInterviewFeedback } from 'constants/interview';
import { SUMMARY_STATUSES } from '_molecules/JobCandidateInfo/JobCandidateSummary/constants';
import { getDocumentsByUUID, getTimeline } from './candidateActions';

const TODAYS_INTERVIEWS_SYNC_KEY = 'ats:todaysInterviews:sync';

const notifyTodaysInterviewsUpdated = (): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(TODAYS_INTERVIEWS_SYNC_KEY, String(Date.now()));
  } catch {
    // Ignore storage errors in private/incognito mode.
  }
};

export interface CandidateStepAssignmentResponse {
  candidate: {
    uuid: Uuid;
    cvs_links?: string[];
    [key: string]: unknown;
  };
  need_sent_decision?: unknown;
  responsible_recruiter?: { uuid: Uuid; full_name?: string } | null;
  status?: string;
  potential_opening?: unknown;
  offer_decision?: unknown;
  opening?: Uuid | null;
  job_assignment: string;
}

interface ProbationScorecardResponse {
  uuid?: Uuid;
  scorecard_items: Array<{ name: string; value: number | string; uuid: Uuid }>;
}

interface OfferRecommendation {
  uuid?: Uuid;
  comment?: string;
  [key: string]: unknown;
}

interface JobValidatePayload {
  name: string;
}

interface InterviewFeedbacksResponse extends Array<InterviewFeedback> {}

interface RoomsOptionParams {
  date: string;
  from_time: string;
  to_time: string;
}

export const getListJobs = (
  body: Record<string, unknown>,
  version: 'v1' | 'v2' = 'v1'
): Promise<PaginatedResponse<JobListItem> | JobListLiteItem[] | null | undefined> =>
  fetchWrapper<PaginatedResponse<JobListItem> | JobListLiteItem[]>({
    url: 'jobs/',
    body,
    version,
    key: 'jobs_list',
  });

export const getOpeningsCount = (
  body: Record<string, unknown>,
  version: 'v1' | 'v2' = 'v2'
): Promise<OpeningsCount | null | undefined> =>
  fetchWrapper<OpeningsCount>({
    url: 'jobs/openings-count/',
    version,
    body,
  });

export const getOpeningsOnTheJobList = (
  uuid: Uuid,
  version: 'v1' | 'v2' = 'v2'
): Promise<Opening[] | null | undefined> =>
  fetchWrapper<Opening[]>({
    url: `jobs/${uuid}/openings-on-job-list/`,
    version,
  });

export const setDataJob = (body: Partial<JobInfo> & { uuid?: Uuid }): Promise<JobInfo | null | undefined> =>
  fetchWrapper<JobInfo>({
    url: `jobs/${body.uuid ? `${body.uuid}/` : ''}`,
    method: body.uuid ? 'PUT' : 'POST',
    body,
    version: 'v2',
  });

export function getOpeningsForDashboard(dashboard: string, url: string, company: string = ''): Promise<unknown> {
  return fetchWrapper({
    url: `dashboard/${dashboard}/${url}?company=${company}`,
    method: 'GET',
  });
}

export function declineApproveOpening(
  uuid: Uuid,
  openingUuid: Uuid,
  type: string,
  body: Record<string, unknown>
): Promise<unknown> {
  return fetchWrapper({
    url: `jobs/${uuid}/openings/${openingUuid}/${type}/`,
    method: 'POST',
    body,
  });
}

export function checkUniqJobName(name: JobValidatePayload): Promise<unknown> {
  return fetchWrapper({
    url: `jobs/validate/`,
    method: 'POST',
    body: name,
  });
}

export function clearJobInfo(): AppThunk {
  return dispatch => dispatch(setJobInfo({}));
}

export function getJobInfo(uuid: Uuid, clearData: boolean = true): AppThunk<Promise<JobInfo | null | undefined>> {
  return dispatch => {
    if (clearData) dispatch(clearJobInfo());
    return fetchWrapper<JobInfo>({
      url: `jobs/${uuid}/`,
      version: 'v2',
    }).then(resp => {
      if (resp) dispatch(setJobInfo(resp));
      return resp;
    });
  };
}

export function assignHrToJobs(uuid: Uuid, recruiters: Uuid[], assignedAsRecruiters?: Uuid[]): AppThunk<Promise<void>> {
  return dispatch => {
    const body: { recruiters: Uuid[]; assigned_as_recruiters?: Uuid[] } = { recruiters };
    if (assignedAsRecruiters !== undefined) {
      body.assigned_as_recruiters = assignedAsRecruiters;
    }
    return fetchWrapper({
      url: `jobs/${uuid}/`,
      method: 'PATCH',
      body,
    })
      .then(() => {
        dispatch(getJobInfo(uuid, false));
      })
      .catch(() => {});
  };
}

export function reassignRecruiterWithActiveCandidates(
  body: Record<string, unknown>,
  jobId: Uuid
): AppThunk<Promise<unknown>> {
  return dispatch =>
    fetchWrapper({
      url: `jobs/unassign-recruiter/`,
      method: 'POST',
      body,
    })
      .then(resp => {
        dispatch(getJobInfo(jobId));
        return resp;
      })
      .catch(() => undefined);
}

export function deleteJobs(uuid: Uuid): AppThunk<Promise<void>> {
  return () =>
    fetchWrapper({
      url: `jobs/${uuid}/`,
      method: 'DELETE',
    })
      .then(() => {
        history.push(ROUTES.JOBS);
      })
      .catch(() => {});
}

export function getSteps(id: Uuid, updateOnlySteps?: boolean): AppThunk<Promise<JobWorkflowStep[] | null | undefined>> {
  return dispatch => {
    dispatch(setStepsLoading(true));
    if (!updateOnlySteps) {
      dispatch(clearCandidatesJob());
    }
    return fetchWrapper<JobWorkflowStep[]>({
      url: `jobs/${id}/workflow/steps/`,
      version: 'v2',
    })
      .then(resp => {
        if (resp) dispatch(setStepsToJob(resp));
        dispatch(setStepsLoading(false));
        return resp;
      })
      .catch(() => {
        dispatch(setStepsLoading(false));
        return undefined;
      });
  };
}

export interface CandidatesJobBody {
  status: string;
  [key: string]: unknown;
}

export interface CandidatesJobResponse {
  results: unknown[];
  count: number;
  next?: unknown;
}

export function getCandidatesJobs(
  id: Uuid,
  stepId: Uuid,
  clear: boolean,
  body: CandidatesJobBody
): AppThunk<Promise<CandidatesJobResponse | null | undefined>> {
  return dispatch => {
    dispatch(setCandidatesLoader({ status: body.status, value: true }));

    return fetchWrapper<CandidatesJobResponse>({
      url: `jobs/${id}/workflow/steps/${stepId}/candidate-step-assignments/`,
      version: 'v2',
      body,
    })
      .then(resp => {
        if (resp) {
          const { results, count, next } = resp;
          dispatch(setCandidatesJob({ results, count, clear, status: body.status, next }));
        }
        dispatch(setStepsLoading(false));
        return resp;
      })
      .finally(() => {
        dispatch(setCandidatesLoader({ status: body.status, value: false }));
      });
  };
}

export function submitOffer(
  jobId: Uuid,
  stepId: Uuid,
  candidateId: Uuid,
  data: Record<string, unknown>
): AppThunk<Promise<void>> {
  return () =>
    fetchWrapper({
      method: 'POST',
      url: `jobs/${jobId}/workflow/steps/${stepId}/candidates/${candidateId}/offer/`,
      body: data,
    })
      .then(() => {})
      .catch(() => {});
}

export const getOpeningsList = (id: Uuid, body?: Record<string, unknown>): Promise<Opening[] | null | undefined> =>
  fetchWrapper<Opening[]>({
    url: `jobs/${id}/openings/`,
    body,
  });

export function getOpenings(
  id: Uuid,
  body?: Record<string, unknown>,
  key?: string
): AppThunk<Promise<Opening[] | null | undefined>> {
  return dispatch => {
    dispatch(setOpeningsLoading(true));
    return fetchWrapper<Opening[]>({
      url: `jobs/${id}/openings/`,
      body,
      key,
    })
      .then(resp => {
        if (resp) dispatch(setOpeningsList(resp));
        return resp;
      })
      .catch(() => undefined)
      .finally(() => dispatch(setOpeningsLoading(false)));
  };
}

export function attachFilesToCandidate(
  body: { cvs: Uuid[]; [key: string]: unknown },
  jobId: Uuid,
  candidateId: Uuid
): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper({
      method: 'PUT',
      version: 'v2',
      url: `candidates/${candidateId}/cvs/`,
      body,
    }).then(() => {
      getDocumentsByUUID(body.cvs).then(cvs => {
        if (cvs) dispatch(updateUserData({ cvs }));
      });
    });
}

export function getOpening(id: Uuid, openingUuid: Uuid): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper<Opening>({
      url: `jobs/${id}/openings/${openingUuid}/`,
    })
      .then(resp => {
        if (resp) dispatch(setOpeningCandidate(resp));
      })
      .catch(() => {})
      .finally(() => dispatch(setLoading(false)));
}

export const getOpeningByUuid = (jobId: Uuid, openingUuid: Uuid): Promise<Opening | null | undefined> =>
  fetchWrapper<Opening>({
    url: `jobs/${jobId}/openings/${openingUuid}/`,
  });

export const setDataOpening = (
  jobId: Uuid,
  openingId: Uuid | undefined,
  body: Partial<Opening>
): Promise<Opening | null | undefined> =>
  fetchWrapper<Opening>({
    url: `jobs/${jobId}/openings/${openingId ? `${openingId}/` : ''}`,
    method: openingId ? 'PUT' : 'POST',
    body,
  });

export const getOpeningsLevelsAPI = (jobId: Uuid): Promise<string[] | null | undefined> =>
  fetchWrapper<string[]>({
    url: `jobs/${jobId}/opening-levels/`,
  });

export function closeOpening(
  uuid: Uuid,
  openingUuid: Uuid,
  body: Record<string, unknown>,
  filters?: Record<string, unknown>
): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper({
      url: `jobs/${uuid}/openings/${openingUuid}/close/`,
      method: 'POST',
      body,
    })
      .then(() => {
        dispatch(getOpenings(uuid, filters));
      })
      .catch(() => {});
}

export function deleteOpening(
  uuid: Uuid,
  openingUuid: Uuid,
  filters?: Record<string, unknown>
): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper({
      url: `jobs/${uuid}/openings/${openingUuid}/`,
      method: 'DELETE',
    })
      .then(() => {
        dispatch(getOpenings(uuid, filters));
      })
      .catch(() => {});
}

export function getOpeningAdditionalInfo(uuid: Uuid, openingUuid: Uuid): Promise<Record<string, unknown> | undefined> {
  return fetchWrapper<Record<string, unknown>>({
    url: `jobs/${uuid}/openings/${openingUuid}/history/`,
    method: 'GET',
  })
    .then(resp => resp ?? undefined)
    .catch(e => e);
}

export function pauseOpening(
  uuid: Uuid,
  openingUuid: Uuid,
  body: Record<string, unknown>,
  filters?: Record<string, unknown>
): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper({
      url: `jobs/${uuid}/openings/${openingUuid}/pause/`,
      method: 'POST',
      body,
    }).then(() => {
      dispatch(getOpenings(uuid, filters));
    });
}

export function reopenOpening(
  uuid: Uuid,
  openingUuid: Uuid,
  body: Record<string, unknown>,
  filters?: Record<string, unknown>
): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper({
      url: `jobs/${uuid}/openings/${openingUuid}/reopen/`,
      method: 'POST',
      body,
    }).then(() => {
      dispatch(getOpenings(uuid, filters));
    });
}

export function submitDecisionWithOppening(
  jobId: Uuid,
  stepId: Uuid,
  candidateId: Uuid,
  body: Record<string, unknown>
): AppThunk<Promise<void>> {
  return () =>
    fetchWrapper({
      method: 'POST',
      url: `jobs/${jobId}/workflow/steps/${stepId}/candidates/${candidateId}/offer-decision/`,
      body,
    })
      .then(() => {})
      .catch(() => {});
}

export function exportToCSV(): AppThunk<Promise<void>> {
  return () =>
    fetchWrapper<{ link: string }>({
      url: `dashboard/resource-manager/export/xlsx/`,
      method: 'GET',
    }).then(res => {
      if (!res) return;
      const link = document.createElement('a');
      link.href = res.link;
      link.click();
    });
}

export function approveDeclineOpening(
  uuid: Uuid,
  openingUuid: Uuid,
  type: string,
  body: Record<string, unknown>,
  filter?: Record<string, unknown>
): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper({
      url: `jobs/${uuid}/openings/${openingUuid}/${type}/`,
      method: 'POST',
      body,
    })
      .then(() => {
        dispatch(getOpenings(uuid, filter));
      })
      .catch(() => {});
}

export function getApprovers(jobId: Uuid, stepId: Uuid, candidateId: Uuid): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper<JobApprover[]>({
      url: `jobs/${jobId}/workflow/steps/${stepId}/candidates/${candidateId}/approvers/`,
    }).then(data => {
      if (data) dispatch(setApproversCandidate(data));
    });
}

export const setApprover = (
  jobId: Uuid,
  stepId: Uuid,
  candidateId: Uuid,
  approverId: Array<{ user: Uuid; optional?: boolean }>
): Promise<void> =>
  fetchWrapper({
    method: 'POST',
    url: `jobs/${jobId}/workflow/steps/${stepId}/candidates/${candidateId}/approvers/`,
    body: {
      step: stepId,
      candidate: candidateId,
      users: approverId,
    },
  })
    .then(() => undefined)
    .catch(() => undefined);

export function getRoomsOption(date: string | Date, fromTime: string, toTime: string): AppThunk {
  const from_time = fromTime.length > 0 ? moment(fromTime, 'HH:mmZ').format('HH:mm') : '12:00';
  const to_time = toTime.length > 0 ? moment(toTime, 'HH:mmZ').format('HH:mm') : '23:30';
  const params: RoomsOptionParams = {
    from_time,
    to_time,
    date: moment(date).format('YYYY-MM-DD'),
  };
  return dispatch => {
    fetchWrapper<Room[]>({
      url: `company-rooms/`,
      body: params,
    }).then(resp => {
      if (resp) dispatch(setRoomsOptions(resp));
    });
  };
}

export const getInterviewFeedbacksAPI =
  (jobId: Uuid, stepId: Uuid, candidateId: Uuid): AppThunk<Promise<InterviewFeedback[] | undefined>> =>
  (dispatch, getState) => {
    dispatch(setInterviewFeedbacksLoading({ stepId, loading: true }));
    return fetchWrapper<InterviewFeedbacksResponse>({
      url: `jobs/${jobId}/workflow/steps/${stepId}/candidates/${candidateId}/interview-feedbacks/`,
      key: `interview-feedbacks-${jobId}-${stepId}`,
    })
      .then(resp => {
        if (!resp) {
          dispatch(setInterviewFeedbacks({ stepId, feedbacks: [] }));
          return undefined;
        }
        const userUuid = getState().authorize.user?.uuid;
        const empty = resp.find(f => !f.comment && f.approver.uuid === userUuid);
        const otherFeedbacks = resp.filter(f => !!f.comment);
        const feedbacks = (empty ? [empty, ...otherFeedbacks] : otherFeedbacks).map(normalizeInterviewFeedback);

        dispatch(setInterviewFeedbacks({ stepId, feedbacks }));
        return feedbacks;
      })
      .catch(() => {
        dispatch(setInterviewFeedbacks({ stepId, feedbacks: [] }));
        return undefined;
      })
      .finally(() => {
        dispatch(setInterviewFeedbacksLoading({ stepId, loading: false }));
      });
  };

export function getOffering(jobId: Uuid, stepId: Uuid, candidateId: Uuid): AppThunk {
  return dispatch => {
    dispatch(setOfferingCandidateLoading(true));
    fetchWrapper<OfferingCandidate[]>({
      url: `jobs/${jobId}/workflow/steps/${stepId}/candidates/${candidateId}/offer/`,
    })
      .then(resp => {
        dispatch(setOfferingCandidate(resp && resp.length ? resp[0] : {}));
        dispatch(setOfferingCandidateLoading(false));
      })
      .catch(() => {
        dispatch(setOfferingCandidateLoading(false));
      });
    fetchWrapper<OfferRecommendation>({
      url: `jobs/${jobId}/workflow/steps/${stepId}/candidates/${candidateId}/recommendation/`,
    })
      .then(resp => {
        dispatch(setOfferRecommendation(resp));
      })
      .catch(() => {
        dispatch(setOfferRecommendation(null));
      });
  };
}

export function getInterviews(jobId: Uuid, stepId: Uuid, candidateId: Uuid): AppThunk<Promise<Interview[]>> {
  return dispatch => {
    dispatch(setInterviewLoading({ stepId, loading: true }));
    return fetchWrapper<Interview[]>({
      url: `jobs/${jobId}/workflow/steps/${stepId}/candidates/${candidateId}/interviews/`,
      key: `interviews-${jobId}-${stepId}`,
    })
      .then(resp => {
        const interviews = resp || [];
        dispatch(setInterviews({ stepId, interviews }));
        return interviews;
      })
      .catch(() => {
        dispatch(setInterviews({ stepId, interviews: [] }));
        return [];
      })
      .finally(() => {
        dispatch(setInterviewLoading({ stepId, loading: false }));
      });
  };
}

const refreshStepInterviewData =
  (jobId: Uuid, stepId: Uuid, candidateId: Uuid): AppThunk =>
  dispatch => {
    dispatch(getInterviews(jobId, stepId, candidateId));
    return dispatch(getInterviewFeedbacksAPI(jobId, stepId, candidateId));
  };

export const getCandidateSummary =
  (candidateId: Uuid, jobId: Uuid): AppThunk<Promise<void>> =>
  dispatch => {
    dispatch(candidateSummaryRequested(candidateId));
    return fetchWrapper<CandidateSummary>({
      url: `candidates/${candidateId}/jobs/${jobId}/feedback-summary/`,
      method: 'GET',
      key: 'candidate-feedback-summary',
      version: 'v2',
    })
      .then(data => {
        dispatch(candidateSummarySucceeded({ candidateId, data: data ?? null }));
      })
      .catch(error => {
        if (isCancel(error)) return;
        dispatch(
          candidateSummaryFailed({
            candidateId,
            status: SUMMARY_STATUSES.EMPTY,
          })
        );
      });
  };

export const updateCandidateSummary =
  (
    candidateId: Uuid,
    jobId: Uuid,
    body: CandidateSummary['content']
  ): AppThunk<Promise<CandidateSummary | null | undefined>> =>
  dispatch => {
    dispatch(candidateSummaryUpdateRequested(candidateId));
    return fetchWrapper<CandidateSummary>({
      url: `candidates/${candidateId}/jobs/${jobId}/feedback-summary/`,
      method: 'PATCH',
      body,
      version: 'v2',
    })
      .then(data => {
        if (data) {
          dispatch(candidateSummarySucceeded({ candidateId, data }));
        } else {
          dispatch(candidateSummaryUpdateFailed(candidateId));
        }
        return data;
      })
      .catch(() => {
        dispatch(candidateSummaryUpdateFailed(candidateId));
        return undefined;
      });
  };

export const generateCandidateSummary =
  (candidateId: Uuid, jobId: Uuid, signal?: AbortSignal): AppThunk<Promise<CandidateSummary | null | undefined>> =>
  dispatch => {
    dispatch(candidateSummaryRequested(candidateId));
    return fetchWrapper<CandidateSummary>({
      url: `candidates/${candidateId}/jobs/${jobId}/feedback-summary/generate/`,
      method: 'POST',
      signal,
      key: 'candidate-feedback-summary',
      version: 'v2',
      showErrorNotification: false,
    })
      .then(data => {
        if (signal?.aborted) return undefined;
        dispatch(candidateSummarySucceeded({ candidateId, data: data ?? null }));
        return data;
      })
      .catch(error => {
        if (signal?.aborted || isCancel(error)) return undefined;
        const { response } = error as {
          response?: { data?: Record<string, string | string[]> };
        };
        if (response) {
          const summaryStatus = response.data?.sources ? SUMMARY_STATUSES.NOT_ENOUGH_DATA : SUMMARY_STATUSES.ERROR;
          dispatch(
            candidateSummaryFailed({
              candidateId,
              status: summaryStatus,
            })
          );
        }
        return undefined;
      });
  };

export const getCandidateStepAssignment =
  (
    jobId: Uuid,
    candidateId: Uuid,
    clearData: boolean,
    steps: JobWorkflowStep[],
    step: number
  ): AppThunk<Promise<void>> =>
  dispatch => {
    dispatch(setLoadingCandidateData(true));
    if (clearData) {
      dispatch(clearInterviewData());
      dispatch(setApproversCandidate([]));
      dispatch(setUserData({}));
      dispatch(setJobStepAssignmentId(''));
      dispatch(setCVSLinks([]));
      dispatch(setCVSCandidate([]));
      dispatch(setOpeningCandidate(null));
      dispatch(setActivityList([]));
      dispatch(setOpeningCandidate(null));
      dispatch(setProbationPeriodData({}));
    }
    return fetchWrapper<CandidateStepAssignmentResponse>({
      url: `jobs/${jobId}/workflow/steps/candidate-step-assignments/candidates/${candidateId}/`,
      version: 'v2',
    })
      .then(resp => {
        if (!resp) return;
        const stepId = steps[step].uuid;
        if (clearData) {
          getFeedbackHistorySteps(steps, steps[step]).forEach(feedbackStep => {
            dispatch(getInterviewFeedbacksAPI(jobId, feedbackStep.uuid, candidateId));
            dispatch(getInterviews(jobId, feedbackStep.uuid, candidateId));
          });

          if (step === 6) dispatch(getOpenings(jobId, { status: 'Open' }));
          if (resp.opening) dispatch(getOpening(jobId, resp.opening));
          if (resp.candidate.cvs_links)
            dispatch(
              setCVSLinks(
                resp.candidate.cvs_links.map(i => ({
                  link: 'CV link',
                  url: i,
                }))
              )
            );
          if (step >= 5 && !!stepId) dispatch(getOffering(jobId, stepId, candidateId));

          if (stepId) {
            dispatch(setApproversCandidateLoading(true));
            fetchWrapper<JobApprover[]>({
              url: `jobs/${jobId}/workflow/steps/${stepId}/candidates/${candidateId}/approvers/`,
            })
              .then(data => {
                dispatch(setApproversCandidateLoading(false));
                if (data) dispatch(setApproversCandidate(data.filter(i => !i.user.is_deleted)));
              })
              .catch(() => {
                dispatch(setApproversCandidateLoading(false));
              });
          }
        }
        dispatch(
          setUserData({
            ...resp.candidate,
            need_sent_decision: resp.need_sent_decision,
            responsible_recruiter: resp.responsible_recruiter,
            step_status: resp.status,
            potential_opening: resp.potential_opening,
            offer_decision: resp.offer_decision,
          })
        );
        dispatch(setJobStepAssignmentId(resp.job_assignment));
        dispatch(setLoadingCandidateData(false));
      })
      .catch(() => {
        dispatch(setOpeningCandidate(null));
        dispatch(setLoadingCandidateData(false));
      });
  };

export function reassignResponsibleRecruiter(
  body: { job: Uuid; candidate: Uuid; [key: string]: unknown },
  steps: JobWorkflowStep[],
  updateInterview: boolean,
  updateStepFullAssignment: boolean,
  step: number
): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper({
      url: `candidates/responsible-recruiters/reassign/`,
      method: 'POST',
      body,
    }).then(() => {
      const activeStep = steps[step].uuid;
      fetchWrapper<JobApprover[]>({
        url: `jobs/${body.job}/workflow/steps/${activeStep}/candidates/${body.candidate}/approvers/`,
      }).then(data => {
        if (data) dispatch(setApproversCandidate(data.filter(i => !i.user.is_deleted)));
      });
      dispatch(getCandidateStepAssignment(body.job, body.candidate, updateStepFullAssignment, steps, step));
      if (updateInterview) {
        dispatch(getInterviews(body.job, activeStep, body.candidate));
        if (getInterviewKind(steps[step].name)) {
          dispatch(getInterviewFeedbacksAPI(body.job, activeStep, body.candidate));
        }
      }
      dispatch(getJobInfo(body.job));
    });
}

export function submitDecision(
  jobId: Uuid,
  stepId: Uuid,
  candidateId: Uuid,
  body: Record<string, unknown>,
  finalDecision?: boolean
): AppThunk<Promise<unknown>> {
  return () =>
    fetchWrapper({
      url: `jobs/${jobId}/workflow/steps/${stepId}/candidates/${candidateId}/${
        finalDecision ? 'final-decision' : 'decisions'
      }/`,
      method: 'POST',
      body: { ...body, candidate: candidateId, step: stepId },
    });
}

export function actionWithdraw(
  jobId: Uuid,
  stepId: Uuid,
  candidateId: Uuid,
  body: Record<string, unknown>
): AppThunk<Promise<void>> {
  return () =>
    fetchWrapper({
      url: `jobs/${jobId}/workflow/steps/${stepId}/candidates/${candidateId}/withdraw/`,
      method: 'POST',
      body,
    })
      .then(() => {})
      .catch(() => {});
}

export const getInterviewFeedbacksDashboardAPI = (
  jobId: Uuid,
  stepId: Uuid,
  candidateId: Uuid,
  userUuid: Uuid
): Promise<InterviewFeedback[] | undefined> =>
  fetchWrapper<InterviewFeedbacksResponse>({
    url: `jobs/${jobId}/workflow/steps/${stepId}/candidates/${candidateId}/interview-feedbacks/`,
  })
    .then(resp => {
      if (!resp) return undefined;
      const empty = resp.find(f => !f.comment && f.approver.uuid === userUuid);
      const otherFeedbacks = resp.filter(f => !!f.comment);
      return empty ? [empty, ...otherFeedbacks] : otherFeedbacks;
    })
    .catch(() => undefined);

export const saveInterviewFeedbacks = (
  jobId: Uuid,
  stepId: Uuid,
  candidateId: Uuid,
  scorecardId: Uuid,
  body: Partial<InterviewFeedback> | object,
  options: { showErrorNotification?: boolean } = {}
): Promise<unknown> =>
  fetchWrapper({
    method: 'PUT',
    url: `jobs/${jobId}/workflow/steps/${stepId}/candidates/${candidateId}/interview-feedbacks/${scorecardId}/`,
    body,
    showErrorNotification: options.showErrorNotification,
  });

export interface ImproveInterviewFeedbackCommentPayload {
  current_comment: string;
  prev_comment?: string;
}

export interface ImproveInterviewFeedbackCommentResponse {
  prev_comment: string;
  improved_comment: string;
}

export const improveInterviewFeedbackComment = (
  jobId: Uuid,
  stepId: Uuid,
  candidateId: Uuid,
  body: ImproveInterviewFeedbackCommentPayload,
  signal?: AbortSignal
): Promise<ImproveInterviewFeedbackCommentResponse> =>
  fetchWrapper<ImproveInterviewFeedbackCommentResponse>({
    method: 'POST',
    url: `jobs/${jobId}/workflow/steps/${stepId}/candidates/${candidateId}/interview-feedbacks/improve-comment/`,
    body,
    signal,
    showErrorNotification: false,
  });

export function assignToStep(
  jobId: Uuid,
  stepId: Uuid,
  candidateId: Uuid,
  body: Record<string, unknown>
): AppThunk<Promise<void>> {
  return () =>
    fetchWrapper({
      method: 'POST',
      url: `jobs/${jobId}/workflow/steps/${stepId}/candidates/${candidateId}/assign-step/`,
      body,
    })
      .then(() => {})
      .catch(() => {});
}

export function getTodaysInterviewsAPI(): AppThunk<Promise<TodaysInterview[]>> {
  return dispatch =>
    fetchWrapper<TodaysInterview[]>({
      url: `todays-interviews/`,
      version: 'v2',
    })
      .then(resp => {
        const list = resp ?? [];
        dispatch(setTodaysnterviews(list));
        return list;
      })
      .catch(() => {
        dispatch(setTodaysnterviews([]));
        return [];
      });
}

export interface SaveInterviewPayload {
  uuid?: Uuid;
  date: string;
  from_time: string;
  to_time: string;
  type?: string;
  link?: string;
  locations?: Array<Uuid | string>;
  approvers?: Uuid[];
  create_google_event?: boolean;
  interview_type?: Interview['interview_type'];
}

export function createInterview(
  jobId: Uuid,
  stepId: Uuid,
  candidateId: Uuid,
  body: SaveInterviewPayload
): AppThunk<Promise<Interview | null>> {
  const { uuid, ...requestBody } = body;
  return dispatch =>
    fetchWrapper<Interview>({
      method: uuid ? 'PUT' : 'POST',
      url: `jobs/${jobId}/workflow/steps/${stepId}/candidates/${candidateId}/interviews/${uuid ? `${uuid}/` : ''}`,
      body: requestBody,
    })
      .then(resp => {
        notifyTodaysInterviewsUpdated();
        if (resp) dispatch(upsertInterview({ stepId, interview: resp }));
        const interview = resp || null;
        dispatch(refreshStepInterviewData(jobId, stepId, candidateId));
        return interview;
      })
      .catch(() => null);
}

export function confirmInterview(
  jobId: Uuid,
  stepId: Uuid,
  candidateId: Uuid,
  interviewId: Uuid
): AppThunk<Promise<unknown>> {
  return dispatch =>
    fetchWrapper({
      method: 'POST',
      url: `jobs/${jobId}/workflow/steps/${stepId}/candidates/${candidateId}/interviews/confirm-interview/`,
      body: {
        interview: interviewId,
      },
    })
      .then(resp => {
        notifyTodaysInterviewsUpdated();
        dispatch(refreshStepInterviewData(jobId, stepId, candidateId));
        return resp;
      })
      .catch(() => null);
}

export function deleteInterview(
  jobId: Uuid,
  stepId: Uuid,
  candidateId: Uuid,
  interviewId: Uuid,
  body: Record<string, unknown>
): AppThunk<Promise<unknown>> {
  return dispatch =>
    fetchWrapper({
      method: 'POST',
      url: `jobs/${jobId}/workflow/steps/${stepId}/candidates/${candidateId}/interviews/${interviewId}/cancel/`,
      body,
    }).then(resp => {
      notifyTodaysInterviewsUpdated();
      dispatch(removeInterview({ stepId, interviewId }));
      dispatch(refreshStepInterviewData(jobId, stepId, candidateId));
      return resp;
    });
}

export const updateHiredCandidate = (
  jobId: Uuid,
  candidateId: Uuid,
  jobAssignment: Uuid,
  body: Record<string, unknown>
): Promise<unknown> =>
  fetchWrapper({
    method: 'PATCH',
    url: `jobs/${jobId}/workflow/probation-passed/candidates/${candidateId}/job_assignments/${jobAssignment}/`,
    body,
  });

export function updateCandidateResolution(
  jobId: Uuid,
  candidateId: Uuid,
  jobAssignment: Uuid,
  body: { opening_id: Uuid; [key: string]: unknown },
  steps: JobWorkflowStep[],
  step: number
): AppThunk {
  return dispatch => {
    dispatch(setEntryDateLoading(true));
    updateHiredCandidate(jobId, candidateId, jobAssignment, body)
      .then(() => {
        dispatch(getCandidateStepAssignment(jobId, candidateId, false, steps, step));
        dispatch(getOpening(jobId, body.opening_id));
      })
      .catch(() => {})
      .finally(() => {
        dispatch(setEntryDateLoading(false));
      });
  };
}

type HiredCandidatesPage = { results: HiredCandidate[]; count: number };

export const getHiredCandidates = (
  jobId: Uuid,
  body?: Record<string, unknown>
): Promise<HiredCandidatesPage | null | undefined> =>
  fetchWrapper<HiredCandidatesPage>({
    url: `jobs/${jobId}/workflow/probation-passed/candidates/`,
    body,
  });

export const getHiredCandidate =
  (jobId: Uuid, candidateId: Uuid): AppThunk<Promise<ProbationPeriod | undefined>> =>
  dispatch =>
    fetchWrapper<ProbationPeriod>({
      method: 'GET',
      url: `jobs/${jobId}/workflow/probation-passed/candidates/${candidateId}/`,
    })
      .then(resp => {
        if (resp) {
          dispatch(setProbationPeriodData(resp));
          return resp;
        }
        return undefined;
      })
      .catch(() => {
        dispatch(setProbationPeriodData({}));
        return undefined;
      });

export function getProbationPassedScorecard(jobId: Uuid, candidateUuid: Uuid): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper<ProbationScorecardResponse[]>({
      url: `jobs/${jobId}/workflow/probation-passed/candidates/${candidateUuid}/scorecards/`,
    })
      .then(resp => {
        if (resp) {
          const scorecards: JobScorecard[] = resp.map(item => ({
            ...item,
            scorecard_items: item.scorecard_items.map(v => ({
              label: v.name,
              value: Number(v.value) / 2,
              id: v.uuid,
            })),
          }));
          dispatch(setScorecardCandidate(scorecards));
        }
      })
      .catch(() => {});
}

export function getProbationPassedStartDate(jobId: Uuid, candidateUuid: Uuid): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper<ProbationPeriod>({
      url: `jobs/${jobId}/workflow/probation-passed/candidates/${candidateUuid}/start-date/`,
    })
      .then(resp => {
        if (resp) dispatch(setProbationPeriodData(resp));
        else dispatch(setProbationPeriodData({}));
      })
      .catch(() => {
        dispatch(setProbationPeriodData({}));
      });
}

export function updateProbationPassedStartDate(
  jobId: Uuid,
  candidateUuid: Uuid,
  body: Record<string, unknown>
): AppThunk<Promise<void>> {
  return dispatch =>
    fetchWrapper<ProbationPeriod>({
      url: `jobs/${jobId}/workflow/probation-passed/candidates/${candidateUuid}/start-date/`,
      method: 'PUT',
      body,
    })
      .then(resp => {
        if (resp) dispatch(setProbationPeriodData(resp));
      })
      .catch(() => {});
}

export function getActivityList(job: Uuid, candidateUuid: Uuid): AppThunk<Promise<ActivityItem[] | null | undefined>> {
  return dispatch => {
    dispatch(setActivityListLoading(true));
    return fetchWrapper<ActivityItem[]>({
      url: `candidates/${candidateUuid}/events/`,
      body: { job },
    })
      .then(resp => {
        if (resp) {
          const list = [...resp].sort((a, b) => new Date(b.created).valueOf() - new Date(a.created).valueOf());
          dispatch(setActivityList(list));
        }
        dispatch(setActivityListLoading(false));
        return resp;
      })
      .catch(() => {
        dispatch(setActivityList([]));
        dispatch(setActivityListLoading(false));
        return undefined;
      });
  };
}

export const changeStateJob =
  (uuid: Uuid, body: Record<string, unknown>): AppThunk<Promise<unknown>> =>
  () =>
    fetchWrapper({
      method: 'PUT',
      url: `jobs/${uuid}/status/`,
      body,
    });

export const getMentionUsers = (uuid: Uuid, candidateUuid: Uuid): Promise<unknown> =>
  fetchWrapper({
    method: 'GET',
    url: `jobs/${uuid}/workflow/candidates/${candidateUuid}/comments/mention-users/`,
  });

export const postCommentTimeline =
  (body: Record<string, unknown>, jobUuid: Uuid, candidateUuid: Uuid): AppThunk<Promise<unknown>> =>
  dispatch => {
    dispatch(setTimelineCommentLoading(true));
    return fetchWrapper({
      method: 'POST',
      url: `jobs/${jobUuid}/workflow/candidates/${candidateUuid}/comments/`,
      body,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
      .then(() => {
        dispatch(getTimeline(candidateUuid, jobUuid, true));
      })
      .finally(() => {
        dispatch(setTimelineCommentLoading(false));
      });
  };

export const deleteCommentTimeline = (commentUuid: Uuid, jobUuid: Uuid, candidateUuid: Uuid): Promise<unknown> =>
  fetchWrapper({
    method: 'DELETE',
    url: `jobs/${jobUuid}/workflow/candidates/${candidateUuid}/comments/${commentUuid}/`,
  });

export function getOpeningById(id: Uuid, openingUuid: Uuid): Promise<Opening | null | undefined> {
  return fetchWrapper<Opening>({
    url: `jobs/${id}/openings/${openingUuid}/`,
  });
}

export function getTechnologyAreas(uuid: Uuid): Promise<TechnologyAreaOption[] | null | undefined> {
  return fetchWrapper<TechnologyAreaOption[]>({
    url: `jobs/${uuid}/openings/options/technology_area/`,
  });
}
